/**
 * TokenBridge - JWT 토큰 Keychain 브릿지
 *
 * iOS 네이티브 Keychain에 JWT 토큰을 저장/조회/삭제하는 서비스입니다.
 * Share Extension과 메인 앱 간에 토큰을 공유하기 위해 사용됩니다.
 *
 * 사용 예시:
 * ```typescript
 * import { saveToken, getToken, clearToken } from './services/tokenBridge';
 *
 * await saveToken('eyJhbGc...');
 * const token = await getToken();
 * await clearToken();
 * ```
 */

import { registerPlugin } from '@capacitor/core';

/**
 * TokenBridge 플러그인 인터페이스
 */
export interface TokenBridgePlugin {
  /**
   * JWT 토큰을 저장소에 저장 (레거시 - 단일 토큰)
   * @param options - { token: string }
   */
  saveToken(options: { token: string }): Promise<{ success: boolean }>;

  /**
   * access_token과 refresh_token을 함께 저장
   * @param options - { accessToken: string, refreshToken: string }
   */
  saveTokens(options: { accessToken: string; refreshToken: string }): Promise<{ success: boolean }>;

  /**
   * 저장소에서 JWT 토큰 조회 (단일 - 레거시)
   * @returns { token: string | null }
   */
  getToken(): Promise<{ token: string | null }>;

  /**
   * 저장소에서 토큰 쌍(access + refresh) 조회
   * Share Extension이 토큰을 갱신했을 때 메인 앱에서 동기화하기 위해 사용
   * @returns { accessToken: string | null, refreshToken: string | null }
   */
  getTokens(): Promise<{ accessToken: string | null; refreshToken: string | null }>;

  /**
   * 저장소에서 모든 토큰 삭제
   */
  clearToken(): Promise<{ success: boolean }>;

  /**
   * 언어 설정을 저장소에 저장
   * Share Extension과 언어 설정을 공유하기 위해 사용
   * @param options - { language: string }
   */
  saveLanguage(options: { language: string }): Promise<{ success: boolean }>;

  /**
   * 저장소에서 언어 설정 조회
   * @returns { language: string } (기본값: "en")
   */
  getLanguage(): Promise<{ language: string }>;

  /**
   * 저장소에서 FCM 토큰 조회
   * Firebase에서 토큰이 수신되면 AppDelegate가 App Groups에 저장
   * Capacitor 리스너가 토큰을 놓친 경우 이 메서드로 캐시된 토큰을 가져옴
   * @returns { fcmToken: string | null }
   */
  getFcmToken(): Promise<{ fcmToken: string | null }>;
}

const TokenBridge = registerPlugin<TokenBridgePlugin>('TokenBridge');

/**
 * JWT 토큰을 저장소에 저장 (레거시 - 단일 토큰)
 *
 * @param token - 저장할 JWT 토큰
 * @throws 저장 실패 시 에러
 */
export async function saveToken(token: string): Promise<void> {
  try {
    await TokenBridge.saveToken({ token });
    console.log('[TokenBridge] 토큰 저장 성공');
  } catch (error) {
    console.error('[TokenBridge] 토큰 저장 실패:', error);
    throw new Error('Failed to save token');
  }
}

/**
 * access_token과 refresh_token을 함께 저장소에 저장
 *
 * @param accessToken - 저장할 access_token
 * @param refreshToken - 저장할 refresh_token
 * @throws 저장 실패 시 에러
 */
export async function saveTokens(accessToken: string, refreshToken: string): Promise<void> {
  try {
    await TokenBridge.saveTokens({ accessToken, refreshToken });
    console.log('[TokenBridge] 토큰 쌍 저장 성공');
  } catch (error) {
    console.error('[TokenBridge] 토큰 쌍 저장 실패:', error);
    throw new Error('Failed to save tokens');
  }
}

/**
 * 저장소에서 JWT 토큰 조회
 *
 * @returns 저장된 토큰 (없으면 null)
 */
export async function getToken(): Promise<string | null> {
  try {
    const result = await TokenBridge.getToken();
    if (result.token) {
      console.log('[TokenBridge] 토큰 조회 성공');
      return result.token;
    } else {
      console.log('[TokenBridge] 토큰 없음');
      return null;
    }
  } catch (error) {
    console.error('[TokenBridge] 토큰 조회 실패:', error);
    return null;
  }
}

/**
 * Keychain에서 JWT 토큰 삭제
 *
 * @throws 삭제 실패 시 에러
 */
export async function clearToken(): Promise<void> {
  try {
    await TokenBridge.clearToken();
    console.log('[TokenBridge] 토큰 삭제 성공');
  } catch (error) {
    console.error('[TokenBridge] 토큰 삭제 실패:', error);
    throw new Error('Failed to clear token from Keychain');
  }
}

/**
 * 저장소에서 토큰 쌍(access + refresh) 조회
 *
 * Share Extension이 토큰을 갱신했을 때 메인 앱에서 동기화하기 위해 사용됩니다.
 *
 * @returns 저장된 토큰 쌍 (없으면 각각 null)
 */
export async function getTokens(): Promise<{
  accessToken: string | null;
  refreshToken: string | null;
}> {
  try {
    const result = await TokenBridge.getTokens();
    if (result.accessToken && result.refreshToken) {
      console.log('[TokenBridge] 토큰 쌍 조회 성공');
    } else {
      console.log('[TokenBridge] 토큰 쌍 없음 또는 불완전');
    }
    return result;
  } catch (error) {
    console.error('[TokenBridge] 토큰 쌍 조회 실패:', error);
    return { accessToken: null, refreshToken: null };
  }
}

/**
 * 언어 설정을 저장소에 저장
 *
 * Share Extension과 언어 설정을 공유하기 위해 사용됩니다.
 *
 * @param language - 저장할 언어 코드 (예: "en", "ko")
 * @throws 저장 실패 시 에러
 */
export async function saveLanguagePreference(language: string): Promise<void> {
  try {
    await TokenBridge.saveLanguage({ language });
    console.log('[TokenBridge] 언어 설정 저장 성공:', language);
  } catch (error) {
    console.error('[TokenBridge] 언어 설정 저장 실패:', error);
    throw new Error('Failed to save language preference');
  }
}

/**
 * 저장소에서 언어 설정 조회
 *
 * @returns 저장된 언어 코드 (기본값: "en")
 */
export async function getLanguagePreference(): Promise<string> {
  try {
    const result = await TokenBridge.getLanguage();
    console.log('[TokenBridge] 언어 설정 조회 성공:', result.language);
    return result.language;
  } catch (error) {
    console.error('[TokenBridge] 언어 설정 조회 실패:', error);
    return 'en';
  }
}

/**
 * 저장소에서 FCM 토큰 조회
 *
 * Firebase에서 토큰이 수신되면 AppDelegate가 App Groups에 저장합니다.
 * Capacitor 리스너가 토큰을 놓친 경우 이 메서드로 캐시된 토큰을 가져올 수 있습니다.
 *
 * @returns 저장된 FCM 토큰 (없으면 null)
 */
export async function getFcmToken(): Promise<string | null> {
  try {
    const result = await TokenBridge.getFcmToken();
    if (result.fcmToken) {
      console.log('[TokenBridge] FCM 토큰 조회 성공');
      return result.fcmToken;
    } else {
      console.log('[TokenBridge] FCM 토큰 없음');
      return null;
    }
  } catch (error) {
    console.error('[TokenBridge] FCM 토큰 조회 실패:', error);
    return null;
  }
}
